/* EX module library for the supplied Vessel 4.2.0 export. No Focal globals. */
class ExModule extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.cleanups = [];
    this.frames = new Set();
    this.startFrame = requestAnimationFrame(() => { if (this.isConnected) this.mount(); });
  }
  disconnectedCallback() {
    cancelAnimationFrame(this.startFrame);
    this.controller?.abort();
    this.cleanups?.forEach(fn => fn());
    this.frames?.forEach(id => cancelAnimationFrame(id));
    this.removeAttribute('data-ready');
  }
  on(target, type, handler, options = {}) {
    target?.addEventListener(type, handler, {...options, signal: this.controller.signal});
  }
  frame(fn) { const id = requestAnimationFrame(t => { this.frames.delete(id); fn(t); }); this.frames.add(id); }
  observe(elements, callback, options = {}) {
    if (!('IntersectionObserver' in window) || this.reduce) { elements.forEach(callback); return; }
    const io = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) { callback(entry.target); io.unobserve(entry.target); }
    }), {threshold: 0.08, ...options});
    elements.forEach(el => io.observe(el));
    this.cleanups.push(() => io.disconnect());
  }
  mount() {
    this.reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.setAttribute('data-ready', '');
    const root = this.querySelector('[id]');
    root?.classList.add('rh-anim');
    const targets = [...this.querySelectorAll('.reveal,.fade,.rh-band,.rh-bands__head,.rh-jr-head,.rh-svc-card,.rh-promise__col,.rh-t-info,.rh-b-info,.rh-cin-panel')];
    this.observe(targets, el => el.classList.add('in'));
    this.observe([...this.querySelectorAll('[data-to]')], el => this.count(el));
    this.querySelectorAll('.rh-pd-rte p').forEach(p => {
      const em = p.querySelector('em,i');
      if (em && em.textContent.trim() === p.textContent.trim()) p.classList.add('rh-pd-figcap');
    });
    this.on(this, 'click', event => {
      const button = event.target.closest('[data-ess-editorial-columns]');
      if (!button) return;
      const collection = button.closest('[data-ess-editorial-collection]');
      if (!collection) return;
      collection.dataset.essEditorialColumns = button.dataset.essEditorialColumns;
      collection.querySelectorAll('button[data-ess-editorial-columns]').forEach(el => {
        const selected = el === button;
        el.classList.toggle('is-selected', selected);
        el.setAttribute('aria-pressed', String(selected));
      });
    });
    if (this.querySelector('[data-wrap]')) this.slider();
    if (this.querySelector('.rh-cin-panel')) this.projects();
    if (this.querySelector('[data-ex-layers]')) this.layers();
  }
  count(el) {
    const to = Number(el.dataset.to);
    if (!Number.isFinite(to)) return;
    const format = value => `${el.dataset.comma ? Math.round(value).toLocaleString() : Math.round(value)}${el.dataset.suffix || ''}`;
    if (this.reduce) { el.textContent = format(to); return; }
    let start;
    const step = now => { start ??= now; const p = Math.min((now-start)/1200,1); el.textContent = format(to*(1-Math.pow(1-p,3))); if (p<1 && this.isConnected) this.frame(step); };
    this.frame(step);
  }
  slider() {
    const wrap = this.querySelector('[data-wrap]'), range = this.querySelector('[data-ex-range]');
    const max = () => Math.max(0, wrap.scrollWidth-wrap.clientWidth);
    const update = () => {
      if (range) { range.value = String(max() ? Math.abs(wrap.scrollLeft)/max()*100 : 0); range.disabled = max() < 1; }
    };
    const scroll = sign => wrap.scrollBy({left: sign * wrap.clientWidth * .85, behavior: this.reduce ? 'instant' : 'smooth'});
    this.on(this.querySelector('[data-prev]'), 'click', () => scroll(-1));
    this.on(this.querySelector('[data-next]'), 'click', () => scroll(1));
    this.on(wrap, 'scroll', update, {passive:true});
    this.on(range, 'input', () => { wrap.scrollLeft = max()*Number(range.value)/100*(getComputedStyle(wrap).direction==='rtl' ? -1 : 1); });
    if ('ResizeObserver' in window) { const ro=new ResizeObserver(update);ro.observe(wrap);this.cleanups.push(()=>ro.disconnect()); }
    this.on(window,'resize',update,{passive:true});
    let drag = null, dragged = false;
    this.on(wrap,'pointerdown',e=>{ if(e.pointerType!=='mouse'||e.button!==0||e.target.closest('button,input,select,product-form-component'))return;drag={x:e.clientX,left:wrap.scrollLeft};dragged=false; });
    this.on(window,'pointermove',e=>{if(!drag)return;const delta=e.clientX-drag.x;if(Math.abs(delta)>6)dragged=true;if(dragged){e.preventDefault();wrap.scrollLeft=drag.left-delta;wrap.classList.add('grabbing');}});
    this.on(window,'pointerup',()=>{drag=null;wrap.classList.remove('grabbing');});
    this.on(window,'pointercancel',()=>{drag=null;dragged=false;wrap.classList.remove('grabbing');});
    this.on(wrap,'dragstart',e=>e.preventDefault());
    this.on(wrap,'click',e=>{if(dragged){e.preventDefault();e.stopPropagation();dragged=false;}},{capture:true});
    update();
  }
  projects() {
    const panels=[...this.querySelectorAll('.rh-cin-panel')], dots=this.querySelector('.rh-cin-dots');
    const anchors=[];
    if(dots){dots.replaceChildren();const group=document.createElement('div');group.className='d';panels.forEach((panel,i)=>{const a=document.createElement('a');a.href=`#${panel.id || (panel.id=`${this.id}-panel-${i}`)}`;a.setAttribute('aria-label',`Project ${i+1}`);this.on(a,'click',e=>{e.preventDefault();panel.scrollIntoView({behavior:this.reduce?'instant':'smooth'});});group.append(a);anchors.push(a);});dots.append(group);}
    if('IntersectionObserver' in window){const io=new IntersectionObserver(entries=>entries.forEach(e=>{e.target.classList.toggle('active',e.isIntersecting);if(e.isIntersecting){const i=panels.indexOf(e.target);anchors.forEach((a,j)=>{a.classList.toggle('on',i===j);a.setAttribute('aria-current',i===j?'true':'false');});}}),{threshold:.5});panels.forEach(p=>io.observe(p));this.cleanups.push(()=>io.disconnect());}else panels.forEach(p=>p.classList.add('active','in'));
    const svg=this.querySelector('.rh-the'),reveal=svg?.querySelector('[id^="rhReveal-"]');
    if(svg&&reveal&&!this.reduce){this.on(svg,'pointermove',e=>{const r=svg.getBoundingClientRect();reveal.setAttribute('cx',String((e.clientX-r.left)/r.width*300));reveal.setAttribute('cy',String((e.clientY-r.top)/r.height*100));});this.on(svg,'pointerenter',()=>svg.classList.add('hov'));this.on(svg,'pointerleave',()=>svg.classList.remove('hov'));}
  }
  layers() {
    const stage=this.querySelector('[data-ex-layers]');
    const images=[...stage.querySelectorAll('img')];
    if(this.reduce || !Element.prototype.animate)return;
    const waits=images.map(img=>img.complete?Promise.resolve():new Promise(resolve=>{this.on(img,'load',resolve,{once:true});this.on(img,'error',resolve,{once:true});}));
    let timeout;
    const bounded = Promise.race([Promise.all(waits),new Promise(resolve=>{timeout=setTimeout(resolve,5000);})]);
    this.cleanups.push(()=>clearTimeout(timeout));
    bounded.then(()=>{
      clearTimeout(timeout);
      if(!this.isConnected||images.some(img=>!img.complete||!img.naturalWidth))return;
      this.observe([stage],()=>{
        const animate=(el,frames,options)=>{if(!el)return;const a=el.animate(frames,options);this.cleanups.push(()=>a.cancel());};
        stage.querySelectorAll('[data-ex-drop]').forEach((el,i)=>animate(el,[{opacity:0,transform:'translateY(-118%)'},{opacity:1,transform:'translateY(0)'}],{duration:1080,delay:80+i*160,easing:'cubic-bezier(.2,.8,.3,1.08)',fill:'backwards'}));
        animate(stage.querySelector('[data-ex-shadow]'),[{opacity:0},{opacity:.95}],{duration:750,delay:920,fill:'backwards'});
        animate(stage.querySelector('[data-ex-copy]'),[{opacity:0,transform:'translateY(20px)'},{opacity:1,transform:'none'}],{duration:800,delay:1200,fill:'backwards'});
        animate(stage.querySelector('[data-ex-off]'),[{opacity:0,transform:'translateY(-118%)',offset:0},{opacity:1,transform:'translateY(0)',offset:.76},{opacity:0,transform:'translateY(0)',offset:1}],{duration:1500,delay:1720,fill:'both'});
        for(const selector of ['[data-ex-on]','[data-ex-glow]'])animate(stage.querySelector(selector),[{opacity:0},{opacity:1}],{duration:860,delay:2660,fill:'backwards'});
      });
    });
  }
}
if(!customElements.get('ex-module'))customElements.define('ex-module',ExModule);
